import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/cms/RichTextEditor";
import BlockRenderer from "@/components/cms/BlockRenderer";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Eye, Pencil, Globe, FileText, ArrowLeft, ExternalLink,
  Layout, Heading as HeadingIcon, Type, Image as ImageIcon, Images, MousePointerClick,
  Video as VideoIcon, Minus, MoveVertical, Save, Rocket,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

const BLOCK_TYPES = [
  { type: "heading", label: "Titre", icon: HeadingIcon },
  { type: "richtext", label: "Texte riche", icon: Type },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "gallery", label: "Galerie", icon: Images },
  { type: "button", label: "Bouton / lien", icon: MousePointerClick },
  { type: "video", label: "Vidéo", icon: VideoIcon },
  { type: "divider", label: "Séparateur", icon: Minus },
  { type: "spacer", label: "Espace", icon: MoveVertical },
];

const DEFAULTS = {
  heading: { text: "Nouveau titre", level: "2", align: "left" },
  richtext: { html: "<p>Votre texte ici…</p>", align: "left" },
  image: { url: "", alt: "", caption: "", width: "full", align: "center" },
  gallery: { images: [], columns: "3" },
  button: { label: "En savoir plus", href: "/", style: "primary", align: "left" },
  video: { url: "" },
  divider: {},
  spacer: { size: 32 },
};

function BlockEditor({ block, onChange }) {
  const d = block.data || {};
  const set = (patch) => onChange(patch);
  const inline = "h-9";

  switch (block.type) {
    case "heading":
      return (
        <div className="space-y-2">
          <Input className={inline} value={d.text || ""} onChange={(e) => set({ text: e.target.value })} placeholder="Texte du titre" data-testid="pg-blk-heading-text" />
          <div className="flex gap-2">
            <Select value={d.level || "2"} onValueChange={(v) => set({ level: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="2">Grand titre</SelectItem><SelectItem value="3">Sous-titre</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "left"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "richtext":
      return <RichTextEditor value={d.html} onChange={(html) => set({ html })} />;
    case "image":
      return (
        <div className="space-y-2">
          <ImageUploader value={d.url ? [d.url] : []} max={1} testId="pg-blk-image"
            onChange={(arr) => set({ url: arr[0] || "" })} />
          <Input className={inline} value={d.alt || ""} onChange={(e) => set({ alt: e.target.value })} placeholder="Texte alternatif (accessibilité / SEO)" />
          <Input className={inline} value={d.caption || ""} onChange={(e) => set({ caption: e.target.value })} placeholder="Légende (facultatif)" />
          <div className="flex gap-2">
            <Select value={d.width || "full"} onValueChange={(v) => set({ width: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="small">Petite</SelectItem><SelectItem value="medium">Moyenne</SelectItem><SelectItem value="full">Pleine largeur</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "center"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "gallery":
      return (
        <div className="space-y-2">
          <ImageUploader value={(d.images || []).map((im) => im.url)} max={8} testId="pg-blk-gallery"
            onChange={(arr) => set({ images: arr.map((url) => ({ url, alt: "" })) })} />
          <Select value={d.columns || "3"} onValueChange={(v) => set({ columns: v })}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="2">2 colonnes</SelectItem><SelectItem value="3">3 colonnes</SelectItem><SelectItem value="4">4 colonnes</SelectItem></SelectContent>
          </Select>
        </div>
      );
    case "button":
      return (
        <div className="space-y-2">
          <Input className={inline} value={d.label || ""} onChange={(e) => set({ label: e.target.value })} placeholder="Libellé du bouton" data-testid="pg-blk-button-label" />
          <Input className={inline} value={d.href || ""} onChange={(e) => set({ href: e.target.value })} placeholder="Lien (/ma-page ou https://…)" data-testid="pg-blk-button-href" />
          <div className="flex gap-2">
            <Select value={d.style || "primary"} onValueChange={(v) => set({ style: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="primary">Plein</SelectItem><SelectItem value="outline">Contour</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "left"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "video":
      return <Input className={inline} value={d.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="Lien YouTube ou Vimeo" data-testid="pg-blk-video-url" />;
    case "spacer":
      return (
        <div className="flex items-center gap-2">
          <Label className="text-sm">Hauteur</Label>
          <Input type="number" className="w-28 h-9" value={d.size || 32} onChange={(e) => set({ size: Number(e.target.value) })} />
          <span className="text-sm text-slate-400">px</span>
        </div>
      );
    default:
      return <p className="text-sm text-slate-400">Aucun réglage pour ce bloc.</p>;
  }
}

function PageEditor({ page, onBack, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: page.title || "", slug: page.slug || "", seo_description: page.seo_description || "",
    in_menu: !!page.in_menu, published: !!page.published, blocks: page.blocks || [],
  }));
  const [saving, setSaving] = useState(false);
  const isNew = !page.id;

  const addBlock = (type) => setForm((f) => ({ ...f, blocks: [...f.blocks, { id: uid(), type, data: { ...DEFAULTS[type] } }] }));
  const updateBlock = (id, patch) => setForm((f) => ({
    ...f, blocks: f.blocks.map((b) => (b.id !== id ? b : { ...b, data: { ...(b.data || {}), ...patch } })),
  }));
  const removeBlock = (id) => setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== id) }));
  const moveBlock = (id, dir) => setForm((f) => {
    const arr = [...f.blocks];
    const i = arr.findIndex((b) => b.id === id);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, blocks: arr };
  });

  const save = async (publish) => {
    if (!form.title.trim()) { toast.error("Donnez un titre à la page."); return; }
    setSaving(true);
    try {
      const payload = { ...form, published: publish };
      const saved = isNew
        ? (await api.post("/pro/minisite/pages", payload)).data
        : (await api.put(`/pro/minisite/pages/${page.id}`, payload)).data;
      toast.success(publish ? "Page publiée ✅" : "Brouillon enregistré (hors ligne jusqu'à publication)");
      onSaved(saved);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement."); }
    setSaving(false);
  };

  return (
    <div data-testid="pg-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="pg-editor-back"><ArrowLeft size={15} /> Retour aux pages</button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={saving} onClick={() => save(false)} data-testid="pg-editor-save-draft"><Save size={15} className="mr-1.5" /> Brouillon</Button>
          <Button size="sm" disabled={saving} onClick={() => save(true)} className="bg-[#C84B31] text-white hover:brightness-110" data-testid="pg-editor-publish"><Rocket size={15} className="mr-1.5" /> Publier</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
            <div><Label>Titre de la page</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ex. Notre histoire" data-testid="pg-title-input" /></div>
            <div><Label>Adresse (slug)</Label>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-slate-400">…/p/</span>
                <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="auto depuis le titre" data-testid="pg-slug-input" />
              </div>
            </div>
            <div><Label>Description SEO</Label><Textarea rows={2} value={form.seo_description} onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))} placeholder="Résumé affiché dans Google (max ~160 caractères)" data-testid="pg-seo-input" /></div>
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
              <div><div className="text-sm font-semibold text-slate-800">Afficher dans le menu du mini-site</div><div className="text-xs text-slate-500">Lien rapide dans l'en-tête de votre mini-site</div></div>
              <Switch checked={form.in_menu} onCheckedChange={(v) => setForm((f) => ({ ...f, in_menu: v }))} data-testid="pg-nav-toggle" />
            </div>
          </div>

          <div className="space-y-3">
            {form.blocks.map((b, i) => {
              const meta = BLOCK_TYPES.find((t) => t.type === b.type);
              const Icon = meta?.icon || Layout;
              return (
                <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`pg-block-edit-${b.type}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><Icon size={14} /> {meta?.label || b.type}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveBlock(b.id, -1)} disabled={i === 0} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30" data-testid={`pg-block-up-${i}`}><ArrowUp size={14} /></button>
                      <button onClick={() => moveBlock(b.id, 1)} disabled={i === form.blocks.length - 1} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30" data-testid={`pg-block-down-${i}`}><ArrowDown size={14} /></button>
                      <button onClick={() => removeBlock(b.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" data-testid={`pg-block-remove-${i}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <BlockEditor block={b} onChange={(data) => updateBlock(b.id, data)} />
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Plus size={14} /> Ajouter un bloc</div>
            <div className="flex flex-wrap gap-2">
              {BLOCK_TYPES.map((t) => (
                <button key={t.type} onClick={() => addBlock(t.type)} data-testid={`pg-add-block-${t.type}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[#C84B31] hover:text-[#C84B31] transition">
                  <t.icon size={14} /> {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-6 self-start">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Eye size={14} /> Aperçu en direct</div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 min-h-[300px]" data-testid="pg-preview">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-5">{form.title || "Titre de la page"}</h1>
            <div className="space-y-5">
              {form.blocks.length === 0 && <p className="text-slate-400 text-sm">Ajoutez des blocs pour composer votre page.</p>}
              {form.blocks.map((b) => <div key={b.id}><BlockRenderer block={b} /></div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProMiniSitePages({ bizId }) {
  const [pages, setPages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/pro/minisite/pages")
    .then(({ data }) => setPages(data.pages || []))
    .catch((e) => toast.error(e?.response?.data?.detail || "Erreur de chargement."))
    .finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Supprimer la page « ${p.title} » ?`)) return;
    try { await api.delete(`/pro/minisite/pages/${p.id}`); toast.success("Page supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/vitrine/${bizId}`;

  if (editing) {
    return <PageEditor page={editing} onBack={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }
  if (!loaded) return <p className="text-slate-400">Chargement…</p>;

  return (
    <div data-testid="pro-pages">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500 max-w-xl">
          Créez des pages personnalisées pour votre mini-site (Notre histoire, Galerie, Événement…). Chacune a sa propre adresse pour un référencement optimal.
        </p>
        <Button onClick={() => setEditing({ title: "", blocks: [] })} className="bg-[#C84B31] text-white hover:brightness-110" data-testid="pg-new-btn">
          <Plus size={15} className="mr-1.5" /> Nouvelle page
        </Button>
      </div>

      {pages.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="pg-empty">
          <FileText size={32} className="mx-auto mb-2 opacity-50" />
          Aucune page personnalisée pour l'instant. Créez votre première page avec le bouton ci-dessus.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="pg-list">
          {pages.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`pg-row-${p.slug}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">{p.title}</div>
                <div className="text-xs text-slate-400">…/p/{p.slug}{p.in_menu ? " · dans le menu" : ""}</div>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {p.published ? "Publiée" : "Brouillon"}
              </span>
              {p.published && (
                <a href={`${base}/p/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Voir en ligne" data-testid={`pg-view-${p.slug}`}><ExternalLink size={16} /></a>
              )}
              <button onClick={() => setEditing(p)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Modifier" data-testid={`pg-edit-${p.slug}`}><Pencil size={16} /></button>
              <button onClick={() => del(p)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" title="Supprimer" data-testid={`pg-delete-${p.slug}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
